import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import KwbLogo from './KwbLogo';

gsap.registerPlugin(ScrollTrigger);

const { FiArrowUpRight, FiMenu, FiLinkedin } = FiIcons;

const HeroSection = () => {
  const sectionRef = useRef(null);
  const contentRef = useRef(null);
  const titleRef = useRef(null);
  const imageRef = useRef(null);
  const navRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // 1. Navbar Entrance
      gsap.from(navRef.current.children, {
        y: -20,
        opacity: 0,
        duration: 0.8,
        stagger: 0.1,
        ease: "power3.out",
        delay: 0.2
      });

      // 2. Title Animation
      gsap.from(".hero-title-word", {
        y: 100,
        opacity: 0,
        duration: 1.2,
        stagger: 0.1,
        ease: "power4.out",
        delay: 0.5
      });

      // 3. Image Reveal
      gsap.from(imageRef.current, {
        scale: 0.9,
        opacity: 0,
        y: 50,
        duration: 1.2,
        ease: "power3.out",
        delay: 0.8
      });

      // 4. Side Elements
      gsap.from(".hero-side-element", {
        opacity: 0,
        x: (i) => i % 2 === 0 ? -20 : 20,
        duration: 1,
        delay: 1.2
      });

      // 5. Footer Elements
      gsap.from(".hero-footer-element", {
        y: 20,
        opacity: 0,
        duration: 0.8,
        stagger: 0.1,
        delay: 1.4
      });

      // 6. Scroll Exit Animation (Smooth Transition to Dark)
      // Animate content out and background to black
      const exitTl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: "bottom 40%", // Trigger earlier for smoother blend
          scrub: true,
        }
      });

      exitTl.to(contentRef.current, {
        y: 100,
        opacity: 0,
        scale: 0.95,
        filter: "blur(5px)", // Cinematic blur
        ease: "power1.inOut"
      }, 0)
      .to(sectionRef.current, {
        backgroundColor: "#000000",
        ease: "power1.inOut"
      }, 0);

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // Smooth Scroll Helper
  const scrollToSection = (id) => (e) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div ref={sectionRef} className="relative min-h-screen bg-[#ffffff] overflow-hidden">
      <div ref={contentRef} className="w-full min-h-screen flex flex-col px-4 md:px-10 pb-6 pt-8 text-black font-sans">
        
        {/* 1. Navigation */}
        <nav ref={navRef} className="w-full flex justify-between items-center text-[12px] font-bold tracking-widest uppercase mb-16 md:mb-12 relative z-50">
          <div className="w-1/4 leading-none flex items-center">
            <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} aria-label="Katherine William-Botrous — back to top" className="inline-flex items-center">
              <KwbLogo variant="black" className="h-7 w-auto md:h-9" />
            </a>
          </div>
          <div className="hidden md:flex gap-10 lg:gap-14 w-2/4 justify-center text-gray-400 font-mono">
            {/* Navigation Links with Smooth Scroll */}
            <a href="#about" onClick={scrollToSection('about')} className="hover:text-black transition-colors whitespace-nowrap cursor-pointer">[ ABOUT ]</a>
            <a href="#works" onClick={scrollToSection('works')} className="hover:text-black transition-colors whitespace-nowrap cursor-pointer">[ CAREER ]</a>
            <a href="#recommendations" onClick={scrollToSection('recommendations')} className="hover:text-black transition-colors whitespace-nowrap cursor-pointer">[ ENDORSEMENTS ]</a>
            <a href="#blog" onClick={scrollToSection('blog')} className="hover:text-black transition-colors whitespace-nowrap cursor-pointer">[ BLOG ]</a>
            <a href="#contact" onClick={scrollToSection('contact')} className="hover:text-black transition-colors whitespace-nowrap cursor-pointer">[ CONNECT ]</a>
            <a href="/resume.pdf" target="_blank" rel="noreferrer" className="hover:text-black transition-colors whitespace-nowrap cursor-pointer" aria-label="Download Katherine William-Botrous résumé">[ RESUME ]</a>
          </div>
          <div className="w-1/4 flex justify-end pt-1 gap-4">
            <a href="https://www.linkedin.com/in/kathy-william-botrous" target="_blank" rel="noreferrer" className="hidden md:block" aria-label="Katherine William-Botrous on LinkedIn">
              <SafeIcon icon={FiLinkedin} className="text-xl hover:opacity-60 transition-opacity" />
            </a>
            <a href="#works" onClick={scrollToSection('works')} className="flex items-center gap-2 border-b border-black pb-0.5 hover:opacity-60 transition-opacity cursor-pointer">
              READ HER STORY <SafeIcon icon={FiArrowUpRight} />
            </a>
          </div>
          {/* Mobile Menu */}
          <div className="md:hidden absolute top-0 right-0">
            <SafeIcon icon={FiMenu} className="text-2xl" />
          </div>
        </nav>

        {/* 2. Main Hero Content */}
        <main className="flex-1 flex flex-col relative w-full max-w-[1800px] mx-auto justify-center">
          {/* Tiny Label Above Title */}
          <div className="hidden md:block absolute top-[-2vw] left-1 text-[11px] font-bold font-mono hero-side-element">
            SALES LEADERSHIP / OPERATIONS
          </div>

          {/* MASSIVE TITLE */}
          <div ref={titleRef} className="relative w-full z-0 leading-[0.8] select-none pointer-events-none mb-5 md:mb-[-5vw]">
            <h1 className="text-black w-full flex flex-col md:flex-row items-start md:items-end justify-between gap-1 md:gap-8 scale-y-[1.05] origin-bottom transform-gpu">
              <span className="hero-title-word whitespace-nowrap text-[11vw] md:text-[7vw] font-extrabold tracking-tight leading-[0.86] md:leading-none">PEOPLE</span>
              <span className="hero-title-word whitespace-nowrap self-end text-[8.4vw] md:text-[4.65vw] font-extrabold tracking-[-0.045em] leading-[0.86] md:leading-none text-right">PERFORMANCE</span>
            </h1>
          </div>

          {/* Middle Composer: Services | Image | Based In */}
          <div className="relative w-full z-10 grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
            {/* Left: Tech Stack List */}
            <div className="md:col-span-4 flex flex-col justify-end h-full pb-0 md:pb-8 pl-1 hero-side-element order-2 md:order-1">
              <div className="flex flex-col gap-2 text-[12px] font-bold uppercase tracking-wide text-gray-900">
                <div className="hover:pl-2 transition-all cursor-pointer">/ SALES LEADERSHIP</div>
                <div className="hover:pl-2 transition-all cursor-pointer">/ CUSTOMER SUCCESS</div>
                <div className="hover:pl-2 transition-all cursor-pointer">/ HIGH-PERFORMANCE TEAMS</div>
              </div>
            </div>

            {/* Center: Image */}
            <div className="md:col-span-4 flex justify-center order-1 md:order-2 h-full items-end">
              <div ref={imageRef} className="relative w-[70%] md:w-[85%] max-w-[360px] aspect-[4/5] bg-[#D4D4D4] shadow-2xl overflow-hidden group">
                <img src="/assets/katherine-headshot.webp" alt="Katherine William-Botrous, Vice President of Sales" className="w-full h-full object-cover contrast-[1.05] brightness-105 scale-105 group-hover:scale-110 transition-transform duration-700" />
              </div>
            </div>

            {/* Right: Location */}
            <div className="md:col-span-4 flex flex-col justify-end items-end h-full pt-4 md:pt-0 md:pb-8 pr-1 hero-side-element order-3">
              <div className="text-[10px] font-bold tracking-[0.4em] uppercase text-right w-full mt-4 md:mt-0">
                BASED IN OKLAHOMA
              </div>
            </div>
          </div>

          {/* Description Text */}
          <div className="w-full flex justify-center mt-12 md:mt-16 mb-8 hero-footer-element">
            <p className="text-center text-[11px] md:text-[13px] font-medium uppercase leading-relaxed tracking-widest text-gray-600 max-w-lg">
              <span className="block">Vice President of Sales at ReminderMedia.</span>
              <span className="block">Full P&amp;L ownership and extreme ownership across sales,</span>
              <span className="block">marketing, design, and product development.</span>
            </p>
          </div>
        </main>

        {/* 3. Footer */}
        <footer className="w-full flex flex-col md:flex-row justify-between items-end gap-6 mt-auto border-t border-transparent pt-4">
          {/* Left */}
          <div className="hero-footer-element group">
            <div className="flex items-center gap-2 mb-2 text-[10px] font-bold uppercase tracking-widest text-gray-500">
              Professional Profile <SafeIcon icon={FiArrowUpRight} className="text-black group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
            </div>
            <a href="mailto:kwb@kwbotrous.com" className="text-sm md:text-md font-bold border-b-2 border-black pb-0.5 hover:text-gray-600 transition-colors">
              kwb@kwbotrous.com
            </a>
          </div>

          {/* Right */}
          <div className="hero-footer-element text-right group">
            <div className="flex items-center justify-end gap-2 mb-2 text-[10px] font-bold uppercase tracking-widest text-gray-500">
              Current Role <SafeIcon icon={FiArrowUpRight} className="text-black group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
            </div>
            <div className="text-2xl md:text-3xl font-black tracking-tighter uppercase leading-none">
              REMINDERMEDIA
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default HeroSection;